import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Clock,
  MapPin,
  Phone,
  RefreshCw,
  ShoppingBag,
  X,
  Volume2,
  VolumeX,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Hash,
  User as UserIcon,
  Printer,
  Settings2,
  TimerOff,
} from 'lucide-react'
import apiClient from '@/api/client'
import type { OnlineOrder } from '@/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/ui/date-picker'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LiveStatusPill } from '@/components/ui/live-status-pill'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { formatDateTimeHuman } from '@/lib/utils'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { buildOnlineOrderKotSlip, printOnlineOrderKot } from '@/lib/printOnlineOrderKot'
import { KotPrintModal } from '@/components/counter/KotPrintModal'
import type { StationKOT } from '@/types'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { kitchenSoundService } from '@/services/soundService'
import { ActiveOrdersBoard, ACTIVE_ONLINE_ORDERS_QUERY_KEY } from '@/components/admin/online-orders/ActiveOrdersBoard'
import { EditOnlineOrderDialog } from '@/components/admin/online-orders/EditOnlineOrderDialog'
import {
  ORDERING_STATUS_QUERY_KEY,
  OrderingStatusBanner,
  OrderingStatusControl,
  useOrderingStatus,
} from '@/components/admin/online-orders/OrderingStatusControl'
import { OnlineOrderSettingsDialog } from '@/components/admin/online-orders/OnlineOrderSettingsDialog'
import { NAG_INTERVAL_LATE_MS, clockTime, minutesBetween, nagIntervalMs } from '@/lib/onlineOrderTiming'
import { getStoredUserRole } from '@/lib/staff-roles'

// ONLINE_ORDERS_QUERY_KEY is shared with the nav badge (AdminLayout) so both
// refetch together and the badge clears the moment an order is actioned.
export const ONLINE_ORDERS_QUERY_KEY = ['online-orders', 'pending'] as const

/** Stream events that change the pending inbox (and so its nav badge/pill). */
export function isOnlineInboxEvent(type: string): boolean {
  return (
    type === 'online_order_placed' ||
    type === 'online_order_updated' ||
    type === 'online_order_accepted' ||
    type === 'online_order_rejected' ||
    // F-ONL-N4: closed by the unanswered-order sweeper.
    type === 'online_order_expired'
  )
}

const FULFILLMENT_LABEL: Record<string, string> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
  dine_in: 'Dine-in',
}

// Quick-pick reasons surfaced on the Reject dialog. Free text is always allowed
// on top; whatever is chosen flows to online_orders.rejected_reason and shows in
// the Rejected history tab.
const REJECT_REASONS = ['Out of stock', 'Closed', 'Too busy', 'Suspicious / spam', "Can't fulfill"] as const

const HISTORY_PER_PAGE = 20

export function AdminOnlineOrders() {
  const queryClient = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [tab, setTab] = useState<'active' | 'accepted' | 'rejected'>('active')
  // The order currently being rejected (drives the reason dialog).
  const [rejectTarget, setRejectTarget] = useState<OnlineOrder | null>(null)
  // The order whose items are being removed (F-FE-N193).
  const [editTarget, setEditTarget] = useState<OnlineOrder | null>(null)
  // KOT-print confirmation (reuses the checkout counter's KotPrintModal). Holds
  // the synthetic ONLINE ORDER slip for the just-accepted order.
  const [kotPrintOpen, setKotPrintOpen] = useState(false)
  const [lastOnlineKots, setLastOnlineKots] = useState<StationKOT[] | undefined>(undefined)
  // F-ONL-N3 / F-ONL-N4: pause / busy, and the unanswered-order window.
  const { status: orderingStatus } = useOrderingStatus()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const role = getStoredUserRole()
  const canEditSettings = role === 'admin' || role === 'manager'

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ONLINE_ORDERS_QUERY_KEY,
    queryFn: () => apiClient.getOnlineOrders().then((r) => r.data ?? []),
    // Poll fallback for when the live stream drops. Critically:
    //  • refetchIntervalInBackground keeps the poll alive while the tab/PWA is
    //    backgrounded (default off → the poll freezes and orders pile up unseen).
    //  • staleTime 0 + refetchOnWindowFocus 'always' make returning to the page
    //    pull immediately, instead of the global 5-min staleTime suppressing it.
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  })

  const orders = data ?? []

  // F-FE-N193: the edit dialog shows the LIVE row, so an edit made on another
  // till shows up in it. If the order leaves the inbox (accepted or rejected
  // elsewhere) while the dialog is open, close it and say why.
  const liveEditOrder = editTarget ? orders.find((o) => o.id === editTarget.id) ?? null : null
  useEffect(() => {
    if (editTarget && data && !data.some((o) => o.id === editTarget.id)) {
      setEditTarget(null)
      toastHelpers.error('Order moved on', 'It was accepted or rejected on another screen.')
    }
  }, [editTarget, data])

  // --- Real-time + audible alerts ----------------------------------------
  // Instant refresh the moment the backend streams a new order; the poll above
  // is just a fallback if the SSE stream is momentarily down. Consume the
  // returned status so the header can show a live/offline pill — a silently
  // dead stream must never look the same as a working one.
  const streamStatus = useDashboardStream({
    onEvent: (ev: DashboardStreamEvent) => {
      if (isOnlineInboxEvent(ev.type)) {
        queryClient.invalidateQueries({ queryKey: ONLINE_ORDERS_QUERY_KEY })
      }
      // Accepted on another screen: it is now on the board (In kitchen) and in
      // the Accepted history. Rejected: it is in the Rejected history.
      if (ev.type === 'online_order_accepted') {
        queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['online-orders', 'history', 'accepted'] })
      }
      if (ev.type === 'online_order_rejected') {
        queryClient.invalidateQueries({ queryKey: ['online-orders', 'history', 'rejected'] })
      }
      // F-ONL-N4: the sweeper closed orders nobody answered — they left the
      // inbox above and land in Rejected.
      if (ev.type === 'online_order_expired') {
        queryClient.invalidateQueries({ queryKey: ['online-orders', 'history', 'rejected'] })
        toastHelpers.error('Order missed', ev.detail || 'An order nobody answered was cancelled and the customer told.')
      }
      // F-ONL-N3: paused, slowed or reopened on another screen. Busy also
      // changes the "ready in" each Incoming card suggests.
      if (ev.type === 'online_ordering_status') {
        queryClient.invalidateQueries({ queryKey: ORDERING_STATUS_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ONLINE_ORDERS_QUERY_KEY })
      }
      // Any order status transition (KDS bump, dispatch tap, payment
      // completing the order) may move a card between board lanes.
      // order_voided: an item came off (F-FE-N193) — the card's lines and total change.
      if (ev.type === 'order_updated' || ev.type === 'order_completed' || ev.type === 'order_cancelled' || ev.type === 'order_voided') {
        queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
      }
    },
  })

  // Belt-and-suspenders for PWA/tablet resume: when the OS freezes a backgrounded
  // app the EventSource and poll both pause and focus events may not fire on
  // return. Refetch the instant the page becomes visible again so the cashier
  // never comes back to a stale inbox.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refetch])

  // Sound state for the header toggle. The service owns enabled/volume (shared,
  // persisted in localStorage); audioState tells us whether the browser has
  // actually unlocked audio yet (it stays 'suspended' until a user gesture).
  const [soundOn, setSoundOn] = useState(() => kitchenSoundService.getSettings().enabled)
  const [audioState, setAudioState] = useState<ReturnType<typeof kitchenSoundService.getAudioState>>(
    () => kitchenSoundService.getAudioState(),
  )
  const refreshAudioState = () => setAudioState(kitchenSoundService.getAudioState())

  // Arm audio on mount, and keep the indicator honest when the tab is re-shown.
  useEffect(() => {
    kitchenSoundService.ensureUnlocked().then(refreshAudioState)
    const onVis = () => refreshAudioState()
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // Chime once per genuinely-new order. The first population is recorded
  // silently so opening the page with a backlog doesn't blast a sound per card.
  // Fires whether the order arrived via the SSE push or the poll fallback.
  const prevIdsRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    if (!data) return
    const ids = new Set(data.map((o) => o.id))
    if (prevIdsRef.current === null) {
      prevIdsRef.current = ids
      return
    }
    const hasNew = data.some((o) => !prevIdsRef.current!.has(o.id))
    prevIdsRef.current = ids
    if (hasNew) {
      kitchenSoundService.playOnlineOrderRing('online-order') // no-ops if muted
      refreshAudioState()
    }
  }, [data])

  // Repeat-until-attended: keep re-chiming while any order is still pending so
  // it can't sit unnoticed. Resets when the pending count changes (a fresh
  // arrival just chimed) and stops once the queue is cleared. F-ONL-N4: once
  // the oldest order has waited past the red line the ring comes twice as
  // often — the loop ticks at the fast rate and skips every other tick until
  // then, so it speeds up without being torn down.
  const oldestPlacedRef = useRef<number | null>(null)
  oldestPlacedRef.current = orders.reduce<number | null>((min, o) => {
    if (o.requested_time) return min // an order for later is not overdue
    const t = new Date(o.created_at).getTime()
    return isNaN(t) ? min : min == null ? t : Math.min(min, t)
  }, null)
  useEffect(() => {
    if (orders.length === 0) return
    let tick = 0
    const id = window.setInterval(() => {
      tick++
      const oldest = oldestPlacedRef.current
      const waited = oldest == null ? null : minutesBetween(oldest, Date.now())
      if (nagIntervalMs(waited) === NAG_INTERVAL_LATE_MS || tick % 2 === 0) {
        kitchenSoundService.playOnlineOrderRing('online-order-nag') // no-ops if muted
      }
    }, NAG_INTERVAL_LATE_MS)
    return () => window.clearInterval(id)
  }, [orders.length])

  // One button, three states: muted → enable; enabled-but-suspended → unlock
  // (this click is the gesture); enabled-and-running → mute.
  const handleSoundButton = () => {
    if (!soundOn) {
      kitchenSoundService.updateSettings({ enabled: true })
      setSoundOn(true)
      kitchenSoundService.ensureUnlocked().then(() => {
        refreshAudioState()
        kitchenSoundService.testSound('new_order') // confirmation chime
      })
    } else if (audioState === 'suspended') {
      kitchenSoundService.ensureUnlocked().then(() => {
        refreshAudioState()
        kitchenSoundService.testSound('new_order')
      })
    } else {
      kitchenSoundService.updateSettings({ enabled: false })
      setSoundOn(false)
    }
  }

  const soundNeedsUnlock = soundOn && audioState === 'suspended'

  // Fetch KOT paper/font settings once and print the ONLINE ORDER slip for an
  // order (pre-accept, accept, or reprint). No backend fire — this is a paper
  // slip only; KDS/kitchen routing happens on Accept in the backend.
  const printKot = useCallback(
    async (order: OnlineOrder) => {
      try {
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
        await printOnlineOrderKot(order, cfg)
      } catch {
        toastHelpers.error('Print KOT', 'Could not print the kitchen ticket.')
      }
    },
    [queryClient],
  )

  const acceptMutation = useMutation({
    // F-ONL-N5: the minutes chosen on the card become the customer's promise.
    mutationFn: ({ order, readyIn }: { order: OnlineOrder; readyIn: number }) =>
      apiClient.acceptOnlineOrder(order.id, readyIn),
    onMutate: ({ order }) => setBusyId(order.id),
    onSuccess: async (res, { order }) => {
      queryClient.invalidateQueries({ queryKey: ONLINE_ORDERS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      // The just-accepted order enters the board's In-kitchen lane.
      queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
      // Refresh the Accepted history tab so the new order shows there.
      queryClient.invalidateQueries({ queryKey: ['online-orders', 'history', 'accepted'] })
      const data = res.data
      if (data?.fire_failed) {
        // B1: the order was accepted (and is real) but never reached the kitchen
        // — e.g. no active station. Warn loudly: staff must open it and fire
        // manually, or the kitchen never sees it and the customer tracker stalls.
        toastHelpers.error(
          'Accepted — but NOT sent to kitchen',
          data?.order?.order_number
            ? `Order #${data.order.order_number} could not reach the kitchen. Open it and fire it manually (check that a kitchen station is active).`
            : 'This order could not reach the kitchen. Fire it manually (check that a kitchen station is active).',
        )
      } else {
        const promised = data?.promised_at ? ` Customer told: ${order.fulfillment_type === 'delivery' ? 'arriving' : 'ready'} by ${clockTime(data.promised_at)}.` : ''
        toastHelpers.success(
          'Order accepted',
          (data?.order?.order_number ? `Sent to kitchen as #${data.order.order_number}.` : 'Sent to the kitchen.') + promised,
        )
      }
      // M3: the customer's promo went invalid between placement and accept, so
      // the order was billed at full price. Warn staff so they can honour it.
      if (data?.promo_failed) {
        toastHelpers.error(
          'Promo could not be applied',
          `Code ${data.promo_code ?? ''} is no longer valid (expired or hit its limit). Billed at full price — apply the discount manually to honour it.`,
        )
      }
      // Print the ONLINE ORDER kitchen slip. The backend already fired the order
      // to the KDS/printer stations on accept (data.kots); here we produce the
      // clearly-labelled "ONLINE ORDER" paper copy. Mirror the checkout counter:
      // when kitchen.confirm_kot_print is on, show KotPrintModal (print/skip);
      // otherwise print silently. Best-effort — never block the accept.
      try {
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        const sd = settingsRes.data as Record<string, unknown>
        // The accepted order carries the freshly-minted order number; stamp it
        // onto the slip (the pre-accept staging row has none).
        const printOrder: OnlineOrder = { ...order, order_number: data?.order?.order_number ?? order.order_number }
        const slip = buildOnlineOrderKotSlip(printOrder)
        const confirm =
          sd?.['kitchen.confirm_kot_print'] === true || sd?.['kitchen.confirm_kot_print'] === 'true'
        if (confirm) {
          setLastOnlineKots([slip])
          setKotPrintOpen(true)
        } else {
          await printKotReceipts([slip], parseReceiptSettings(sd))
        }
      } catch {
        /* printing is best-effort — never block the accept */
      }
    },
    onError: (err: unknown) => {
      const e = err as { message?: string; code?: string }
      if (e?.code === 'previous_day_open') {
        toastHelpers.error('Close the previous business day', 'A prior day is still open — close it before accepting online orders.')
      } else if (e?.code === 'day_not_open') {
        // F-CTRL-N32: auto-open removed — accepting an online order is a sale
        // and must attach to a day somebody actually started.
        toastHelpers.error('Start the day first', "Today hasn't been started yet. Open the day on the Daily Closing page (or at the counter), then accept the order.")
      } else {
        toastHelpers.error('Could not accept order', e?.message || 'Please try again.')
      }
    },
    onSettled: () => setBusyId(null),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => apiClient.rejectOnlineOrder(id, reason),
    onMutate: ({ id }) => setBusyId(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ONLINE_ORDERS_QUERY_KEY })
      // Refresh the Rejected history tab so the just-rejected order appears there.
      queryClient.invalidateQueries({ queryKey: ['online-orders', 'history', 'rejected'] })
      setRejectTarget(null)
      toastHelpers.success('Order rejected')
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      toastHelpers.error('Could not reject order', e?.message || 'Please try again.')
    },
    onSettled: () => setBusyId(null),
  })

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Unmissable one-time unlock: browsers keep audio suspended until a user
          gesture, so the very first order rings silently otherwise. A full-width
          banner (not just the small header button) makes it obvious; clicking it
          IS the gesture that unlocks audio for the rest of the session. */}
      {soundNeedsUnlock && (
        <button
          type="button"
          onClick={handleSoundButton}
          className="flex w-full items-center justify-center gap-2 bg-amber-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600 motion-safe:animate-pulse"
        >
          <Volume2 className="h-4 w-4" />
          Tap here to enable order alert sounds — new orders won’t ring until you do
        </button>
      )}

      {/* F-ONL-N3: paused or busy — impossible to miss, one tap to undo. */}
      <OrderingStatusBanner status={orderingStatus} />

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as typeof tab)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* Compact header — title on the left (lg+; below lg the mobile top rail
            already shows it), tab nav + live/sound/refresh grouped on the right.
            Merges what used to be a separate heading row and tab bar into one. */}
        <div className="flex flex-col gap-2 border-b border-border px-3 py-2 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:px-6 lg:py-3">
          <div className="hidden min-w-0 lg:block">
            <h1 className="flex items-center gap-2 text-xl font-semibold leading-tight text-foreground">
              <ShoppingBag className="h-5 w-5 shrink-0" />
              Online Orders
            </h1>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              Accept incoming orders, then move each one across the board.
            </p>
          </div>

          {/* Wraps on a phone: the tabs, the ordering switch and the controls
              no longer fit one narrow row. */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 lg:justify-end">
            <TabsList className="no-scrollbar max-w-full justify-start overflow-x-auto [&>*]:shrink-0">
              <TabsTrigger value="active">
                Active
                {orders.length > 0 && (
                  <Badge className="ml-2 bg-red-600 text-white">{orders.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="accepted">Accepted</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
            </TabsList>

            {/* Hairline divider — visually separates tab nav from the live/controls
                cluster once they share a single row (lg+). */}
            <div className="hidden h-6 w-px bg-border lg:block" aria-hidden="true" />

            <div className="flex items-center gap-1.5">
              {/* F-ONL-N3: take orders / busy / paused. On every tab — a pause
                  must be liftable from wherever the cashier is looking. */}
              <OrderingStatusControl status={orderingStatus} />
              {/* Live/offline of the real-time stream — a dead stream must be visible. */}
              <LiveStatusPill status={streamStatus} />
              {canEditSettings && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => setSettingsOpen(true)}
                  aria-label="Online order settings"
                  title="Online order settings"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              )}
              {tab === 'active' && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleSoundButton}
                    className={`h-9 w-9 ${soundNeedsUnlock ? 'border-amber-500 text-amber-600 hover:bg-warning-surface hover:text-warning-fg motion-safe:animate-pulse' : ''}`}
                    aria-label={
                      !soundOn
                        ? 'Sound off — tap to enable order alerts'
                        : soundNeedsUnlock
                          ? 'Tap to enable sound (the browser needs one tap first)'
                          : 'Sound on — tap to mute'
                    }
                    title={
                      !soundOn
                        ? 'Sound off — click to enable order alerts'
                        : soundNeedsUnlock
                          ? 'Click to enable sound (the browser needs one tap first)'
                          : 'Sound on — click to mute'
                    }
                  >
                    {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => {
                      refetch()
                      queryClient.invalidateQueries({ queryKey: ACTIVE_ONLINE_ORDERS_QUERY_KEY })
                    }}
                    disabled={isFetching}
                    aria-label="Refresh orders"
                    title="Refresh"
                  >
                    <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <TabsContent value="active" className="m-0 min-h-0 flex-1 overflow-hidden p-2.5 lg:p-4">
          <ActiveOrdersBoard
            pending={orders}
            pendingLoading={isLoading}
            busyId={busyId}
            onAccept={(o, readyIn) => acceptMutation.mutate({ order: o, readyIn })}
            onReject={(o) => setRejectTarget(o)}
            onEdit={(o) => setEditTarget(o)}
            onPrintKot={printKot}
            formatCurrency={formatCurrency}
            active={tab === 'active'}
          />
        </TabsContent>

        <TabsContent value="accepted" className="m-0 min-h-0 flex-1 overflow-auto p-6">
          <OnlineOrderHistory
            status="accepted"
            formatCurrency={formatCurrency}
            active={tab === 'accepted'}
            onPrintKot={printKot}
          />
        </TabsContent>

        <TabsContent value="rejected" className="m-0 min-h-0 flex-1 overflow-auto p-6">
          <OnlineOrderHistory status="rejected" formatCurrency={formatCurrency} active={tab === 'rejected'} />
        </TabsContent>
      </Tabs>

      <RejectDialog
        order={rejectTarget}
        busy={!!rejectTarget && busyId === rejectTarget.id}
        onCancel={() => setRejectTarget(null)}
        onConfirm={(reason) => rejectTarget && rejectMutation.mutate({ id: rejectTarget.id, reason })}
      />

      <EditOnlineOrderDialog
        order={liveEditOrder}
        onClose={() => setEditTarget(null)}
        formatCurrency={formatCurrency}
        pendingQueryKey={ONLINE_ORDERS_QUERY_KEY}
      />

      {/* KOT-print confirmation for a just-accepted online order (only shown when
          kitchen.confirm_kot_print is on). Reuses the checkout counter modal. */}
      <KotPrintModal open={kotPrintOpen} onOpenChange={setKotPrintOpen} kots={lastOnlineKots} />

      {canEditSettings && (
        <OnlineOrderSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          currentMinutes={orderingStatus.unanswered_expire_minutes}
        />
      )}
    </div>
  )
}

// RejectDialog collects an optional reason (preset chips + free text) before a
// reject is committed. The chosen text is what lands in the Rejected history;
// leaving it blank rejects with the backend's generic default.
function RejectDialog({
  order,
  busy,
  onCancel,
  onConfirm,
}: {
  order: OnlineOrder | null
  busy: boolean
  onCancel: () => void
  onConfirm: (reason?: string) => void
}) {
  const [chip, setChip] = useState<string | null>(null)
  const [text, setText] = useState('')

  // Reset the form whenever a new order opens the dialog.
  useEffect(() => {
    if (order) {
      setChip(null)
      setText('')
    }
  }, [order])

  // Free text wins over a chip; both blank → undefined (backend default).
  const reason = text.trim() || chip || undefined

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject this order?</DialogTitle>
          <DialogDescription>
            {order?.customer_name || 'Guest'} · {order?.cart.length ?? 0} item{order?.cart.length === 1 ? '' : 's'}.
            Add a reason so it’s recorded in the Rejected log (optional).
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          {REJECT_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setChip((cur) => (cur === r ? null : r))}
              className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                chip === r
                  ? 'border-red-500 bg-danger-surface text-danger-fg'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note (optional)…"
          maxLength={200}
          rows={2}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(reason)}
            disabled={busy}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            <X className="mr-1.5 h-4 w-4" />
            {busy ? 'Rejecting…' : 'Reject order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// OnlineOrderHistory powers the Accepted / Rejected tabs: a search + date-range
// filter bar over the paginated /counter/online-orders/history endpoint, newest
// first. Mirrors the Void Log's filter/pager shape.
function OnlineOrderHistory({
  status,
  formatCurrency,
  active,
  onPrintKot,
}: {
  status: 'accepted' | 'rejected'
  formatCurrency: (n: number) => string
  active: boolean
  onPrintKot?: (order: OnlineOrder) => void
}) {
  const [page, setPage] = useState(1)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')

  // Debounce the name/phone search; reset to page 1 on a new term.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(t)
  }, [q])

  const params = useMemo(
    () => ({
      status,
      page,
      per_page: HISTORY_PER_PAGE,
      from: from || undefined,
      to: to || undefined,
      q: debouncedQ || undefined,
    }),
    [status, page, from, to, debouncedQ],
  )

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: ['online-orders', 'history', status, { page, from, to, q: debouncedQ }],
    queryFn: () => apiClient.getOnlineOrderHistory(params),
    enabled: active,
    placeholderData: keepPreviousData,
  })

  const orders = data?.data ?? []
  const meta = data?.meta
  const totalPages = meta?.total_pages ?? 1
  const total = meta?.total ?? 0
  const hasFilters = !!(from || to || debouncedQ)

  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v)
    setPage(1)
  }

  const clearFilters = () => {
    setFrom('')
    setTo('')
    setQ('')
    setDebouncedQ('')
    setPage(1)
  }

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Search name or phone</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Ali or 0300…" className="pl-8" />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
          <DatePicker value={from} onChange={onFilterChange(setFrom)} max={to || undefined} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
          <DatePicker value={to} onChange={onFilterChange(setTo)} min={from || undefined} />
        </div>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{total} total</span>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>
      ) : isError ? (
        <p className="py-16 text-center text-sm text-red-600">Couldn’t load history. Try refreshing.</p>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          {status === 'accepted' ? (
            <CheckCircle2 className="mb-3 h-10 w-10 text-muted-foreground/40" />
          ) : (
            <XCircle className="mb-3 h-10 w-10 text-muted-foreground/40" />
          )}
          <p className="text-base font-medium text-foreground">
            No {status} orders{hasFilters ? ' match these filters' : ' yet'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {orders.map((o) => (
            <HistoryOrderCard
              key={o.id}
              order={o}
              status={status}
              formatCurrency={formatCurrency}
              onPrintKot={onPrintKot}
            />
          ))}
        </div>
      )}

      {/* Pager */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || isFetching}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {meta?.current_page ?? page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

// HistoryOrderCard is the read-only counterpart of OrderCard for the history
// tabs: no Accept/Reject buttons; instead a status footer with who accepted +
// the resulting order number, or the rejection reason.
function HistoryOrderCard({
  order,
  status,
  formatCurrency,
  onPrintKot,
}: {
  order: OnlineOrder
  status: 'accepted' | 'rejected'
  formatCurrency: (n: number) => string
  onPrintKot?: (order: OnlineOrder) => void
}) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary">
              {FULFILLMENT_LABEL[order.fulfillment_type] ?? order.fulfillment_type}
            </Badge>
            {/* F-BE-N54 — same on the history card, so "which table was that?"
                is answerable after the fact too. */}
            {order.table_number && <Badge>Table {order.table_number}</Badge>}
          </div>
          <p className="font-semibold text-foreground">{order.customer_name || 'Guest'}</p>
        </div>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          {formatDateTimeHuman(order.created_at)}
        </span>
      </div>

      <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">
        {order.customer_phone && (
          <p className="flex items-center gap-1.5">
            <Phone className="h-3.5 w-3.5" />
            {order.customer_phone}
          </p>
        )}
        {order.delivery_address && (
          <p className="flex items-start gap-1.5">
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {order.delivery_address}
          </p>
        )}
        {(order.delivery_zone_name || (order.delivery_lat != null && order.delivery_lng != null)) && (
          <p className="flex flex-wrap items-center gap-1.5">
            {order.delivery_zone_name && (
              <Badge variant="secondary" className="font-normal">
                {order.delivery_zone_name}
                {order.delivery_area && order.delivery_area !== order.delivery_zone_name ? ` · ${order.delivery_area}` : ''}
                {order.delivery_eta_minutes ? ` · ~${order.delivery_eta_minutes} min` : ''}
              </Badge>
            )}
            {order.delivery_zone_name && (order.delivery_lat == null || order.delivery_lng == null) && (
              <span className="text-xs text-amber-600 dark:text-amber-500">
                No map pin — customer picked the area; call to confirm the exact address
              </span>
            )}
            {order.delivery_lat != null && order.delivery_lng != null && (
              <a
                href={`https://maps.google.com/?q=${order.delivery_lat},${order.delivery_lng}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                View pin on map
              </a>
            )}
          </p>
        )}
      </div>

      <ul className="mt-3 space-y-1 border-t border-border pt-3 text-sm">
        {order.cart.map((line, i) => (
          <li key={i} className="flex justify-between gap-2">
            <span className="min-w-0 text-foreground">
              <span className="font-medium tabular-nums">{line.quantity}×</span> {line.name}
              {(line.modifier_labels?.length ?? 0) > 0 && (
                <span className="block pl-5 text-xs text-muted-foreground">{line.modifier_labels!.join(', ')}</span>
              )}
              {line.special_instructions && (
                <span className="block pl-5 text-xs italic text-muted-foreground">“{line.special_instructions}”</span>
              )}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">{formatCurrency(line.unit_price * line.quantity)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm text-muted-foreground">Total</span>
        <span className="text-base font-semibold tabular-nums text-foreground">{formatCurrency(order.total_amount)}</span>
      </div>

      {status === 'accepted' ? (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1 font-medium text-success-fg">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Accepted
          </span>
          {order.order_number && (
            <span className="flex items-center gap-1">
              <Hash className="h-3.5 w-3.5" />
              Order #{order.order_number}
            </span>
          )}
          {order.accepted_by_name && (
            <span className="flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" />
              {order.accepted_by_name}
            </span>
          )}
          {onPrintKot && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto h-7"
              onClick={() => onPrintKot(order)}
            >
              <Printer className="mr-1.5 h-3.5 w-3.5" />
              Print KOT
            </Button>
          )}
        </div>
      ) : order.status === 'expired' ? (
        // F-ONL-N4: nobody answered in time; the sweeper closed it.
        <div className="mt-3 border-t border-border pt-3 text-xs">
          <span className="flex items-center gap-1 font-medium text-warning-fg">
            <TimerOff className="h-3.5 w-3.5" />
            Missed — nobody answered in time
          </span>
          <p className="mt-1 text-muted-foreground">Cancelled automatically; the customer was told.</p>
        </div>
      ) : (
        <div className="mt-3 border-t border-border pt-3 text-xs">
          <span className="flex items-center gap-1 font-medium text-danger-fg">
            <XCircle className="h-3.5 w-3.5" />
            Rejected
          </span>
          {order.rejected_reason && <p className="mt-1 text-muted-foreground">Reason: {order.rejected_reason}</p>}
        </div>
      )}
    </div>
  )
}
